import { Fragment, type ReactNode, useState } from 'react';
import type { FigureCell, FigureDefinition, FigureRows } from 'types/aiChatFigure';
import { cn } from 'utils/cn';
import { FigureCellValue } from './FigureCellValue';
import { columnLabel } from './figureMeta';
import { cellText } from './figureRows';
import { ShowMoreButton } from './FigureTable';

// A ranking shows its top three, a search's matches up to ten; "Show more" opens the rest in place.
const NUMBERED_ROWS = 3;
const PLAIN_ROWS = 10;

const ROW_CLASS_NAME = 'border-divider-soft hover:bg-row-stripe border-t px-4 py-2.5';
const VALUE_CLASS_NAME = 'text-meta text-foreground-secondary text-end tabular-nums';

interface Props {
    definition: FigureDefinition;
    rows: FigureRows;
}

/**
 * A list Figure's body: one row per record, its chip, the detail line under it and its one value at the end. A
 * numbered list ranks its rows under a column header; a plain list has none. A list of records that had nothing to
 * count has no value: its rows end with the chip and the detail line. A list has no header actions.
 */
export function FigureList({ definition, rows }: Props) {
    const [open, setOpen] = useState(false);
    const { columns } = rows;
    const at = (field: string | undefined) => columns.findIndex((column) => column.field === field);
    const recordAt = Math.max(0, at(definition.record));
    const valued = !!definition.value;
    const valueIndex = at(definition.value?.field);
    const valueAt = valueIndex < 0 ? columns.length - 1 : valueIndex;
    const detailAt = (definition.detail ?? []).map(at).filter((index) => index >= 0);
    const valueLabel = definition.value?.label ?? columnLabel(columns[valueAt]);
    const numbered = definition.numbered === true;
    const limit = numbered ? NUMBERED_ROWS : PLAIN_ROWS;
    const list = (items: ReactNode, start: number) =>
        numbered ? (
            <ol start={start} className="m-0 list-none p-0">
                {items}
            </ol>
        ) : (
            <ul className="m-0 list-none p-0">{items}</ul>
        );

    const cellAt = (row: FigureCell[], index: number, listPart?: 'detail' | 'value') => (
        <FigureCellValue
            cell={row[index] ?? null}
            column={columns[index]}
            listPart={listPart}
            chipClassName="text-meta"
        />
    );

    const detailOf = (row: FigureCell[]): ReactNode => {
        const parts = detailAt.filter((index) => columns[index].notSet || cellText(row[index] ?? null) !== '');
        return parts.length ? (
            // A line naming several records wraps: a list has no full size and no export to read a cut name in.
            <span
                className={cn(
                    'text-caption text-foreground-tertiary max-w-full min-w-0 ps-1.5',
                    parts.some((index) => Array.isArray(row[index])) ? 'break-words' : 'truncate'
                )}
            >
                {parts.map((index, position) => (
                    <Fragment key={columns[index].field}>
                        {position > 0 ? ' · ' : null}
                        {/* A record named in the detail line reads as its name, the line's own type. */}
                        {columns[index].type === 'record' ? cellText(row[index] ?? null) : cellAt(row, index, 'detail')}
                    </Fragment>
                ))}
            </span>
        ) : null;
    };

    const value = (row: FigureCell[]) => (
        <>
            <span className="sr-only">{valueLabel} </span>
            {cellAt(row, valueAt, 'value')}
        </>
    );

    const item = (row: FigureCell[], index: number) =>
        numbered ? (
            <li key={index} className={`${ROW_CLASS_NAME} flex items-center gap-3`}>
                <span className="text-meta text-foreground-tertiary w-5 shrink-0 text-center tabular-nums">
                    {index + 1}
                </span>
                <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                    {cellAt(row, recordAt)}
                    {detailOf(row)}
                </span>
                <span className={`${VALUE_CLASS_NAME} shrink-0`}>{value(row)}</span>
            </li>
        ) : (
            <li
                key={index}
                className={cn(
                    ROW_CLASS_NAME,
                    'grid items-center gap-x-3 gap-y-0.5',
                    valued && 'grid-cols-[minmax(0,1fr)_auto]'
                )}
            >
                <span className={cn('flex min-w-0', valued && 'col-span-2 @lg:col-span-1')}>
                    {cellAt(row, recordAt)}
                </span>
                {detailOf(row)}
                {valued ? (
                    <span className={`${VALUE_CLASS_NAME} @lg:col-start-2 @lg:row-span-2 @lg:row-start-1`}>
                        {value(row)}
                    </span>
                ) : null}
            </li>
        );

    const rest = rows.rows.slice(limit);

    return (
        <>
            {numbered ? (
                <div
                    aria-hidden="true"
                    className="border-divider-soft text-caption text-foreground-tertiary flex items-center gap-3 border-t px-4 py-1"
                >
                    <span className="w-5 shrink-0 text-center">#</span>
                    <span className="min-w-0 flex-1">{columnLabel(columns[recordAt])}</span>
                    <span className="shrink-0 text-end">{valueLabel}</span>
                </div>
            ) : null}
            {list(rows.rows.slice(0, limit).map(item), 1)}
            {rest.length ? (
                <div className="t-acc" data-open={open ? 'true' : 'false'}>
                    <div className="t-acc-panel">
                        <div className="t-acc-panel-inner">
                            {list(
                                rest.map((row, index) => item(row, limit + index)),
                                limit + 1
                            )}
                        </div>
                    </div>
                    <div className="border-divider-soft flex border-t px-4 py-2.5">
                        <ShowMoreButton open={open} more={rest.length} onToggle={() => setOpen(!open)} />
                    </div>
                </div>
            ) : null}
        </>
    );
}
